/**
 * A conversa do VITA — os metadados de chat de cada step.
 *
 * Fonte: handoff/redesign/Objetivo.dc.html e handoff/12-prompt-objetivo-vita.md § 3 e § 4.
 *
 * ISTO NÃO É O FLUXO. O fluxo continua sendo o `baseSteps` do
 * `FormsObjetivo.jsx`: mesma orquestração, mesmo `saveUserObjective`, mesmo
 * auto-advance. Este arquivo só diz, para cada step:
 *
 *   - `pergunta`: a linha que o VITA escreve;
 *   - `rotulo(answers)`: o que aparece na bolha do usuário DEPOIS de responder,
 *     lido dos campos que o step já grava — nada de campo novo;
 *   - `comentario(answers)`: a réplica do VITA, quando o handoff prevê uma;
 *   - `projecao`: marca o step que dispara o card de projeção.
 *
 * Módulo puro de propósito: é copy e derivação de rótulo, o tipo de coisa que
 * quebra calado quando um campo muda de nome. Tem teste.
 */

import { LOGICAL_LABELS_PT } from '../treinos/components/muscle-map/muscleMapConfig.js';

/** Rótulos legíveis dos valores que o step de objetivo grava. */
export const OBJETIVOS = {
  'ganho muscular': 'Ganhar massa muscular',
  emagrecer: 'Emagrecer',
  'ganho peso': 'Ganhar peso',
  'controle peso': 'Manter o peso',
  'alimentacao saudavel': 'Comer melhor',
  'controle doenca': 'Controlar uma condição de saúde',
};

/** Rótulos do nível de atividade (os `value` são os que o backend lê). */
export const NIVEIS = {
  'muito baixo': 'Sedentário',
  baixo: 'Leve',
  medio: 'Moderado',
  alto: 'Intenso',
};

/** Rótulos do local de treino. */
export const LOCAIS = {
  academia: 'Academia completa',
  casa: 'Em casa, com halteres',
  peso_corpo: 'Só o peso do corpo',
};

/** Rótulos das refeições do dia. */
export const REFEICOES = {
  cafe: 'Café da manhã',
  lanche_manha: 'Lanche da manhã',
  pre_treino: 'Pré-treino',
  almoco: 'Almoço',
  lanche_tarde: 'Lanche da tarde',
  pos_treino: 'Pós-treino',
  jantar: 'Jantar',
  ceia: 'Ceia',
};

/** Rótulos dos grupos musculares. A fonte é o próprio mapa — duas listas de
    nome de músculo divergem no dia em que alguém renomear um grupo lá. */
export const MUSCULOS = LOGICAL_LABELS_PT;

/** Comentários do VITA por objetivo (06-copys / § 4 do prompt). */
const COMENTARIO_DO_OBJETIVO = {
  'ganho muscular':
    'Boa. Ganhar massa pede constância no treino e comer um pouco mais — eu cuido dos dois.',
  emagrecer:
    'Boa. Vamos de déficit leve e treino que preserva massa — sem dieta maluca.',
};

const COMENTARIO_PADRAO_DO_OBJETIVO = 'Anotado. Isso muda como eu monto sua dieta.';

/**
 * Rótulo dos três campos "boolean + detalhe" (saúde, restrição, preferência).
 *
 * O step de hoje grava SEMPRE um boolean no campo-bandeira e o texto no
 * `_DETAILS`. A tolerância a string existe para dado ANTIGO: documentos
 * gravados antes desta evolução guardavam "Sim"/"Não" ali, e o offcanvas de
 * refazer objetivo pode topar com eles.
 */
function rotuloBooleanComDetalhe(bandeira, detalhe, rotuloDoNao) {
  if (bandeira === false) return rotuloDoNao;
  if (detalhe) return detalhe;
  if (typeof bandeira === 'string' && bandeira.trim()) return bandeira;
  return null;
}

/** Junta lista com vírgula e "e". */
function lista(valores, dicionario) {
  const itens = (Array.isArray(valores) ? valores : [valores])
    .filter(Boolean)
    .map((v) => (dicionario ? dicionario[v] || v : v));
  if (!itens.length) return '';
  if (itens.length === 1) return String(itens[0]);
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

const numero = (v) => {
  // `Number('')` é 0 e `Number('   ')` também: sem esta guarda, campo vazio
  // virava "0 kg" na bolha — pior que bolha nenhuma, porque parece um dado.
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(String(v).replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ',');
};

/**
 * O mapa. A chave é o `chatId` gravado em cada componente por
 * `registrarConversa` — ver a nota lá embaixo sobre por que não é `.name`.
 */
export const CONVERSA = {
  StepName: {
    pergunta: 'Antes de tudo: como você se chama, e quando nasceu?',
    rotulo: (a) => {
      const nome = [a.NOME, a.SOBRENOME].filter(Boolean).join(' ');
      if (!nome) return null;
      if (!a.DATA_NASC) return nome;
      const [ano, mes, dia] = String(a.DATA_NASC).split('-');
      return `${nome} · ${dia}/${mes}/${ano}`;
    },
  },

  StepWelcome: {
    pergunta: (nome) =>
      `Oi${nome ? `, ${nome}` : ''}! Eu sou o VITA, seu coach aqui no app. `
      + 'Vou fazer algumas perguntas rápidas e montar um plano de treino e '
      + 'dieta só pra você. Bora?',
    rotulo: (a) =>
      (a.ONBOARDING_ENTRADA === 'so_treino'
        ? 'Quero só o treino por enquanto'
        : a.ONBOARDING_ENTRADA
          ? 'Bora!'
          : null),
    comentario: () => 'Fechado. Leva menos de 2 minutos.',
  },

  StepQuestionObjective: {
    pergunta: 'Qual é o seu objetivo principal?',
    rotulo: (a) => OBJETIVOS[a.QUESTION_OBJECTIVE] || a.QUESTION_OBJECTIVE || null,
    comentario: (a) =>
      COMENTARIO_DO_OBJETIVO[a.QUESTION_OBJECTIVE] || COMENTARIO_PADRAO_DO_OBJETIVO,
  },

  StepGender: {
    // Sexo BIOLÓGICO, e não identidade: é o que entra na taxa metabólica
    // basal e no % de gordura. Ver a nota no topo de StepGender.jsx.
    pergunta: 'Qual o seu sexo biológico?',
    rotulo: (a) => (a.GENDER ? a.GENDER[0].toUpperCase() + a.GENDER.slice(1) : null),
  },

  StepCurrentHeight: {
    pergunta: 'Qual a sua altura? Arrasta a régua.',
    rotulo: (a) => (numero(a.CURRENT_HEIGHT) ? `${numero(a.CURRENT_HEIGHT)} cm` : null),
  },

  StepCurrentWeight: {
    pergunta: 'E o seu peso hoje?',
    rotulo: (a) => (numero(a.CURRENT_WEIGHT) ? `${numero(a.CURRENT_WEIGHT)} kg` : null),
  },

  StepTargetWeight: {
    pergunta: 'Onde você quer chegar?',
    rotulo: (a) => (numero(a.TARGET_WEIGHT) ? `${numero(a.TARGET_WEIGHT)} kg` : null),
  },

  StepTimeframe: {
    pergunta: 'Em que ritmo?',
    rotulo: (a) => a.TIMEFRAME || null,
    projecao: true,
  },

  StepActivityLevel: {
    pergunta: 'Qual seu nível de experiência com treino?',
    rotulo: (a) => NIVEIS[a.ACTIVITY_LEVEL] || a.ACTIVITY_LEVEL || null,
  },

  StepInterest: {
    pergunta: 'Quer priorizar algum grupo muscular? Toca no mapa.',
    rotulo: (a) => {
      if (!Array.isArray(a.INTEREST)) return a.INTEREST || null;
      return a.INTEREST.length ? lista(a.INTEREST, MUSCULOS) : 'Sem preferência';
    },
    comentario: (a) =>
      (Array.isArray(a.INTEREST) && a.INTEREST.length
        ? 'Anotado. Esses grupos entram com um pouco mais de volume.'
        : ''),
  },

  StepDiasTreino: {
    pergunta: 'Quais dias da semana você vai treinar?',
    rotulo: (a) => {
      const dias = a.TRAINING_DAYS;
      if (!Array.isArray(dias) || !dias.length) return null;
      return `${dias.length}x por semana · ${dias.join(', ')}`;
    },
  },

  StepTempoTreino: {
    pergunta: 'Quanto tempo por treino?',
    // O valor gravado é "curto - De 20 a 40 minutos"; na bolha vai só a
    // descrição, que é a parte que a pessoa escolheu de fato.
    rotulo: (a) => (a.SESSION_TIME ? String(a.SESSION_TIME).split(' - ').pop() : null),
  },

  StepLocalTreino: {
    pergunta: 'Onde você vai treinar?',
    rotulo: (a) => LOCAIS[a.TRAINING_PLACE] || a.TRAINING_PLACE || null,
    comentario: (a) =>
      (a.TRAINING_PLACE && a.TRAINING_PLACE !== 'academia'
        ? 'Sem problema. Monto só com o que você tem à mão.'
        : ''),
  },

  StepRefeicoes: {
    pergunta: 'Quais refeições você costuma fazer no dia?',
    rotulo: (a) =>
      (Array.isArray(a.MEALS_PER_DAY) && a.MEALS_PER_DAY.length
        ? lista(a.MEALS_PER_DAY, REFEICOES)
        : null),
  },

  StepResAlimentares: {
    pergunta: 'Tem algo que você não come, alergia ou intolerância?',
    rotulo: (a) =>
      rotuloBooleanComDetalhe(
        a.RESTRICAO_ALIMENTAR,
        a.RESTRICAO_ALIMENTAR_DETAILS,
        'Como de tudo'
      ),
  },

  StepPrefAlimentares: {
    pergunta: 'Tem alguma dieta ou estilo alimentar?',
    rotulo: (a) =>
      rotuloBooleanComDetalhe(
        a.PREFERENCIA_ALIMENTAR,
        a.PREFERENCIA_ALIMENTAR_DETAILS,
        'Nenhuma em especial'
      ),
  },

  StepSpecialCare: {
    pergunta: 'Alguma condição de saúde ou lesão que eu deva considerar?',
    rotulo: (a) =>
      rotuloBooleanComDetalhe(a.SPECIAL_CARE, a.SPECIAL_CARE_DETAILS, 'Não, tudo certo'),
    comentario: (a) =>
      (a.SPECIAL_CARE_DETAILS
        ? 'Vou adaptar os exercícios pra proteger essa região.'
        : ''),
  },

  StepWeightLossMedication: {
    pergunta: 'Você usa alguma caneta emagrecedora atualmente?',
    rotulo: (a) => ({
      sim_acompanhamento: 'Sim, com acompanhamento',
      sim_sem_acompanhamento: 'Sim, sem acompanhamento',
      nao: 'Não',
      prefiro_nao_informar: 'Prefiro não responder',
    })[a.WEIGHT_LOSS_MEDICATION] || null,
    comentario: (a) => (String(a.WEIGHT_LOSS_MEDICATION || '').startsWith('sim_')
      ? 'Entendi. Vou priorizar refeições nutritivas e toleráveis, sem mexer em prescrição.'
      : ''),
  },

  StepSchedule: {
    pergunta: 'Pra fechar: que horas você acorda, dorme e quer treinar?',
    rotulo: (a) => {
      const s = a.SCHEDULE;
      if (!s?.WAKETIME || !s?.SLEEPTIME) return null;
      const treino = s.EXERCISETIME ? ` · treino ${s.EXERCISETIME}` : '';
      return `Acordo ${s.WAKETIME} · durmo ${s.SLEEPTIME}${treino}`;
    },
    comentario: (a) => {
      const hora = a.SCHEDULE?.EXERCISETIME;
      if (!hora) return '';
      const h = Number(String(hora).split(':')[0]);
      const periodo = h < 12 ? 'manhã' : h < 18 ? 'tarde' : 'noite';
      return `Anotado: ${periodo}, às ${hora}. Vou te lembrar 30 min antes.`;
    },
  },

  StepConclusion: {
    pergunta: (nome) =>
      `Pronto${nome ? `, ${nome}` : ''}. Tenho tudo que preciso — confere se está certo.`,
  },
};

/**
 * Liga cada componente de step à sua chave aqui, gravando `chatId` nele.
 *
 * Por que não usar `Componente.name`: o terser do build de produção NÃO
 * preserva nome de função por padrão. Em dev funcionaria e em produção o chat
 * ficaria sem pergunta nenhuma — falha que só apareceria no APK. O `chatId` é
 * uma propriedade explícita, e sobrevive à minificação.
 *
 * @param {object} mapa { chaveDaConversa: ComponenteDoStep }
 */
export function registrarConversa(mapa) {
  Object.entries(mapa || {}).forEach(([chave, Componente]) => {
    if (Componente) Componente.chatId = chave;
  });
}

/** Metadados do step, ou um objeto vazio para step sem copy de chat. */
export function conversaDoStep(Componente) {
  if (!Componente) return {};
  // `name` fica como rede em dev; em produção quem manda é o `chatId`.
  return CONVERSA[Componente.chatId] || CONVERSA[Componente.name] || {};
}

/**
 * A pergunta do step, já resolvida (algumas dependem do nome).
 * @param {Function} Componente step
 * @param {string} nome primeiro nome do usuário
 * @returns {string} a linha do VITA
 */
export function perguntaDoStep(Componente, nome) {
  const { pergunta } = conversaDoStep(Componente);
  if (!pergunta) return '';
  return typeof pergunta === 'function' ? pergunta(nome) : pergunta;
}

/**
 * O histórico da conversa: uma linha por step já respondido.
 *
 * O rótulo sai de `answers`, que é o MESMO objeto que vai para o
 * `saveUserObjective` — então a bolha nunca mostra algo diferente do que foi
 * salvo. Step sem rótulo (intro, conclusão) entra só com a pergunta.
 *
 * @param {object} args
 * @param {Array}  args.steps lista de componentes na ordem do fluxo
 * @param {number} args.ate índice do step ATIVO (exclusivo)
 * @param {object} args.answers respostas acumuladas
 * @param {string} args.nome primeiro nome
 * @returns {Array} linhas do chat
 */
export function historicoDaConversa({ steps = [], ate = 0, answers = {}, nome = '' }) {
  const linhas = [];
  for (let i = 0; i < Math.min(ate, steps.length); i += 1) {
    const Componente = steps[i];
    const meta = conversaDoStep(Componente);
    const pergunta = perguntaDoStep(Componente, nome);
    if (!pergunta) continue;

    const resposta = meta.rotulo ? meta.rotulo(answers) : null;
    const comentario = meta.comentario ? meta.comentario(answers) : '';

    linhas.push({
      id: Componente.chatId || Componente.name || `step${i}`,
      indice: i,
      pergunta,
      resposta: resposta || null,
      comentario: comentario || '',
      projecao: !!meta.projecao,
    });
  }
  return linhas;
}

export default CONVERSA;
